import {
  Input,
  DatePicker,
  Button,
  Row,
  Col,
  Card,
  Table,
  Select,
  Modal,
  message,
  Spin,
  Descriptions,
} from "antd";
import {
  DownloadOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";
import { useState, useContext, useEffect } from "react";
import axios from "axios";
import ViewOneEmployeeRecord from "../../components/hr/ViewOneEmployeeRecord";
import dayjs from "dayjs";
import * as XLSX from "xlsx-js-style";

const { Option } = Select;
const { RangePicker } = DatePicker;

const defaultFilters = {
  employeeCode: "",
  employeeName: "",
  organisation: "",
  department: "",
  designation: "",
  fromDate: null,
  toDate: null,
};

export default function ViewEmployeeAttendance() {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { authData, backUrl } = useContext(AuthContext);

  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showLead, setShowLead] = useState(false);
  const [selectedLeadDetails, setSelectedLeadDetails] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);

  // Filter options from backend
  const [organisations, setOrganisations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);

  // "Draft" filters — what the user is currently typing/selecting
  const [draftFilters, setDraftFilters] = useState({ ...defaultFilters });

  // "Applied" filters — what was last searched (used for actual API calls)
  const [appliedFilters, setAppliedFilters] = useState({ ...defaultFilters });

  const [showSingle, setShowSingle] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // --- Fetch Organisations ---
  const fetchOrganisations = async () => {
    try {
      const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
        headers: { Authorization: `Bearer ${authData?.access}` },
      });
      setOrganisations(res.data.results || res.data);
    } catch (err) {
      console.error("Failed to fetch organisations:", err);
      message.error("Failed to fetch organisations");
    }
  };

  // --- Fetch Departments ---
  const fetchDepartments = async (orgId) => {
    if (!orgId) {
      setDepartments([]);
      return;
    }
    try {
      const res = await axios.get(
        `${backUrl}/api/emp/departments/?organisation=${orgId}`,
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      setDepartments(res.data.results || res.data);
    } catch (err) {
      console.error("Failed to fetch departments:", err);
      message.error("Failed to fetch departments");
      setDepartments([]);
    }
  };

  // --- Fetch Designations ---
  const fetchDesignations = async (deptId) => {
    if (!deptId) {
      setDesignations([]);
      return;
    }
    try {
      const res = await axios.get(
        `${backUrl}/api/emp/designations/?department=${deptId}`,
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      setDesignations(res.data.results || res.data);
    } catch (err) {
      console.error("Failed to fetch designations:", err);
      message.error("Failed to fetch designations");
      setDesignations([]);
    }
  };

  // --- Build params from a filter object ---
  const buildParams = (filters, page, size) => {
    const params = { page, page_size: size };
    if (filters.employeeCode) params.emp_code = filters.employeeCode;
    if (filters.employeeName) params.emp_name = filters.employeeName;
    if (filters.organisation) params.organisation = filters.organisation;
    if (filters.department) params.department = filters.department;
    if (filters.designation) params.designation = filters.designation;
    if (filters.fromDate)
      params.from_date = dayjs(filters.fromDate).format("YYYY-MM-DD");
    if (filters.toDate)
      params.to_date = dayjs(filters.toDate).format("YYYY-MM-DD");
    return params;
  };

  // --- Fetch Attendance Data ---
  const fetchData = async (filters, page, size) => {
    setLoading(true);
    try {
      const params = buildParams(filters, page, size);

      const res = await axios.get(`${backUrl}/api/hr/attendance_records`, {
        headers: { Authorization: `Bearer ${authData.access}` },
        params,
      });

      const records = res.data.results || res.data.attendance_records || [];
      const count = res.data.count || records.length;

      setAttendanceData(records);
      setTotalRecords(count);
    } catch (err) {
      console.error("Error fetching attendance data", err);
      message.error("Failed to load attendance data");
      setAttendanceData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  // --- On mount: fetch organisations + initial data ---
  useEffect(() => {
    fetchOrganisations();
    fetchData(defaultFilters, 1, 100);
  }, [authData?.access, backUrl]);

  // --- When pagination changes, re-fetch with current applied filters ---
  useEffect(() => {
    fetchData(appliedFilters, currentPage, pageSize);
  }, [currentPage, pageSize]);

  // --- Load departments when draft organisation changes (for dropdown population only) ---
  useEffect(() => {
    if (draftFilters.organisation) {
      fetchDepartments(draftFilters.organisation);
    } else {
      setDepartments([]);
    }
  }, [draftFilters.organisation]);

  // --- Load designations when draft department changes (for dropdown population only) ---
  useEffect(() => {
    if (draftFilters.department) {
      fetchDesignations(draftFilters.department);
    } else {
      setDesignations([]);
    }
  }, [draftFilters.department]);

  // --- Handle draft filter changes ---
  const handleFilterChange = (key, value) => {
    setDraftFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "organisation") {
        next.department = "";
        next.designation = "";
      } else if (key === "department") {
        next.designation = "";
      }
      return next;
    });
  };

  // --- Apply filters (Search button) ---
  const handleSearch = () => {
    setAppliedFilters({ ...draftFilters });
    setCurrentPage(1);
    setSelectedRowKeys([]);
    fetchData(draftFilters, 1, pageSize);
  };

  // --- Clear all filters ---
  const handleClear = () => {
    setDraftFilters({ ...defaultFilters });
    setAppliedFilters({ ...defaultFilters });
    setCurrentPage(1);
    setSelectedRowKeys([]);
    setDepartments([]);
    setDesignations([]);
    fetchData(defaultFilters, 1, pageSize);
  };

  // --- Handle pagination/table change ---
  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    setSelectedRowKeys([]);
  };

  // --- Download ---
  const handleDownload = async () => {
    try {
      message.loading("Preparing export...", 0);

      if (selectedRowKeys.length > 0) {
        const rowsToExport = attendanceData.filter((row) =>
          selectedRowKeys.includes(row.id)
        );
        exportToExcel(rowsToExport);
        return;
      }

      const params = buildParams(appliedFilters, 1, 999999);

      const res = await axios.get(`${backUrl}/api/hr/attendance_records`, {
        headers: { Authorization: `Bearer ${authData.access}` },
        params,
      });

      const allRecords = res.data.results || res.data.attendance_records || [];
      message.destroy();
      exportToExcel(allRecords);
    } catch (err) {
      message.destroy();
      console.error("Error exporting data:", err);
      message.error("Failed to export data");
    }
  };

  const exportToExcel = (records) => {
    if (!records.length) {
      message.warning("No records to export");
      return;
    }

    const XLSXStyle = require("xlsx-js-style");

    const headers = [
      "Employee Code",
      "Employee Name",
      "Company Name",
      "Department",
      "Designation",
      "Date",
      "Work Location",
      "Check-In Time",
      "Check-In Location",
      "Check-In Site",
      "Check-In Photo URL",
      "Check-Out Time",
      "Check-Out Location",
      "Check-Out Site",
      "Check-Out Photo URL",
      "Total Time",
      "Status",
    ];

    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, name: "Arial", sz: 11 },
      fill: { fgColor: { rgb: "0E497B" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "FFFFFF" } },
        bottom: { style: "thin", color: { rgb: "FFFFFF" } },
        left: { style: "thin", color: { rgb: "FFFFFF" } },
        right: { style: "thin", color: { rgb: "FFFFFF" } },
      },
    };

    const presentStyle = {
      font: { name: "Arial", sz: 10, color: { rgb: "155724" } },
      fill: { fgColor: { rgb: "D4EDDA" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "C3E6CB" } },
        bottom: { style: "thin", color: { rgb: "C3E6CB" } },
        left: { style: "thin", color: { rgb: "C3E6CB" } },
        right: { style: "thin", color: { rgb: "C3E6CB" } },
      },
    };

    const absentStyle = {
      font: { name: "Arial", sz: 10, color: { rgb: "721C24" } },
      fill: { fgColor: { rgb: "F8D7DA" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "F5C6CB" } },
        bottom: { style: "thin", color: { rgb: "F5C6CB" } },
        left: { style: "thin", color: { rgb: "F5C6CB" } },
        right: { style: "thin", color: { rgb: "F5C6CB" } },
      },
    };

    const defaultStyle = {
      font: { name: "Arial", sz: 10 },
      alignment: { vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "DDDDDD" } },
        bottom: { style: "thin", color: { rgb: "DDDDDD" } },
        left: { style: "thin", color: { rgb: "DDDDDD" } },
        right: { style: "thin", color: { rgb: "DDDDDD" } },
      },
    };

    const altRowStyle = {
      ...defaultStyle,
      fill: { fgColor: { rgb: "F5F8FC" } },
    };

    const wsData = [];
    const headerRow = headers.map((h) => ({ v: h, t: "s", s: headerStyle }));
    wsData.push(headerRow);

    records.forEach((row, rowIdx) => {
      const status = row.status || (row.check_in_time ? "P" : "A");
      const isPresent = status === "P" || status?.toLowerCase() === "present";
      const isAbsent = status === "A" || status?.toLowerCase() === "absent";
      const rowBaseStyle = rowIdx % 2 === 0 ? defaultStyle : altRowStyle;
      const statusCellStyle = isPresent
        ? presentStyle
        : isAbsent
          ? absentStyle
          : rowBaseStyle;
      const statusLabel = isPresent
        ? "P - Present"
        : isAbsent
          ? "A - Absent"
          : status || "-";

      const dataRow = [
        { v: row.emp_code || "-", t: "s", s: rowBaseStyle },
        { v: row.employee_name || "-", t: "s", s: rowBaseStyle },
        { v: row.company_name || row.company || "-", t: "s", s: rowBaseStyle },
        { v: row.department || "-", t: "s", s: rowBaseStyle },
        { v: row.designation || "-", t: "s", s: rowBaseStyle },
        {
          v: row.date ? dayjs(row.date).format("DD/MM/YYYY") : "-",
          t: "s",
          s: rowBaseStyle,
        },
        { v: row.check_in_work_site || "-", t: "s", s: rowBaseStyle },
        { v: row.check_in_time || "-", t: "s", s: rowBaseStyle },
        { v: row.check_in_location || "-", t: "s", s: rowBaseStyle },
        { v: row.check_in_work_site || "-", t: "s", s: rowBaseStyle },
        { v: row.check_in_photo_url || "-", t: "s", s: rowBaseStyle },
        { v: row.check_out_time || "-", t: "s", s: rowBaseStyle },
        { v: row.check_out_location || "-", t: "s", s: rowBaseStyle },
        { v: row.check_out_work_site || "-", t: "s", s: rowBaseStyle },
        { v: row.check_out_photo_url || "-", t: "s", s: rowBaseStyle },
        { v: row.total_time || "-", t: "s", s: rowBaseStyle },
        { v: statusLabel, t: "s", s: statusCellStyle },
      ];

      wsData.push(dataRow);
    });

    const ws = XLSXStyle.utils.aoa_to_sheet(
      wsData.map((row) => row.map((cell) => cell.v))
    );

    const colLetters = "ABCDEFGHIJKLMNOPQ".split("");
    wsData.forEach((row, rIdx) => {
      row.forEach((cell, cIdx) => {
        const cellRef = `${colLetters[cIdx]}${rIdx + 1}`;
        if (!ws[cellRef]) ws[cellRef] = {};
        ws[cellRef].s = cell.s;
      });
    });

    ws["!cols"] = [
      { wch: 14 },
      { wch: 22 },
      { wch: 22 },
      { wch: 18 },
      { wch: 18 },
      { wch: 14 },
      { wch: 16 },
      { wch: 14 },
      { wch: 28 },
      { wch: 16 },
      { wch: 36 },
      { wch: 15 },
      { wch: 28 },
      { wch: 16 },
      { wch: 36 },
      { wch: 12 },
      { wch: 14 },
    ];

    ws["!rows"] = [{ hpt: 36 }];
    for (let i = 1; i <= records.length; i++) {
      ws["!rows"].push({ hpt: 22 });
    }

    ws["!freeze"] = { xSplit: 0, ySplit: 1 };

    const wb = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(wb, ws, "Attendance");
    XLSXStyle.writeFile(wb, "Employee_Attendance.xlsx");
    message.success(`Exported ${records.length} records successfully`);
  };

  // --- Table Columns ---
  const columns = [
    {
      title: "Employee Code",
      dataIndex: "emp_code",
      key: "emp_code",
      render: (text, record) => (
        <a
          onClick={() => {
            setSelectedEmployee(record);
            setShowSingle(true);
          }}
        >
          {text}
        </a>
      ),
    },
    { title: "Employee Name", dataIndex: "employee_name", key: "employee_name" },
    {
      title: "Company Name",
      dataIndex: "company_name",
      key: "company_name",
      render: (text, record) => text || record.company || "-",
    },
    { title: "Department", dataIndex: "department", key: "department" },
    { title: "Designation", dataIndex: "designation", key: "designation" },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
    },
    {
      title: "Work Location",
      dataIndex: "check_in_work_site",
      key: "check_in_work_site",
      render: (site, record) =>
        site === "On-Duty" ? (
          <>
            <p style={{ marginBottom: 4 }}>{site}</p>
            {record?.onduty_type_check_in && (
              <p style={{ marginBottom: 4, fontSize: "12px", color: "#666" }}>
                {record.onduty_type_check_in}
              </p>
            )}
            {record?.onduty_type_check_in === "customer" &&
              record?.lead_details &&
              record.lead_details.length > 0 && (
                <a
                  onClick={() => {
                    setSelectedLeadDetails(record.lead_details);
                    setShowLead(true);
                  }}
                  style={{ color: "#1677ff", cursor: "pointer" }}
                >
                  Lead Details
                </a>
              )}
          </>
        ) : (
          <p>{site}</p>
        ),
    },
    { title: "Check-In Time", dataIndex: "check_in_time", key: "check_in_time" },
    {
      title: "Check-In Location",
      key: "check_in_location",
      render: (record) =>
        record.check_in_latitude && record.check_in_longitude ? (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${record.check_in_latitude},${record.check_in_longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#1677ff" }}
          >
            {record.check_in_location || "View Location"}
          </a>
        ) : (
          record.check_in_location || "-"
        ),
    },
    {
      title: "Check-In Site",
      dataIndex: "check_in_work_site",
      key: "check_in_work_site_2",
      render: (site, record) =>
        site === "On-Duty" ? (
          <>
            <p style={{ marginBottom: 4 }}>{site}</p>
            {record?.onduty_type_check_in && (
              <p style={{ marginBottom: 4, fontSize: "12px", color: "#666" }}>
                {record.onduty_type_check_in}
              </p>
            )}
          </>
        ) : (
          <p>{site}</p>
        ),
    },
    {
      title: "Check-In Photo",
      dataIndex: "check_in_photo_url",
      key: "check_in_photo_url",
      render: (url) =>
        url ? (
          <img
            src={url}
            alt="Check-In"
            style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 8 }}
          />
        ) : (
          <span>No Image</span>
        ),
    },
    { title: "Check-Out Time", dataIndex: "check_out_time", key: "check_out_time" },
    {
      title: "Check-Out Location",
      key: "check_out_location",
      render: (record) =>
        record.check_out_latitude && record.check_out_longitude ? (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${record.check_out_latitude},${record.check_out_longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#1677ff" }}
          >
            {record.check_out_location || "View Location"}
          </a>
        ) : (
          record.check_out_location || "-"
        ),
    },
    {
      title: "Check-Out Site",
      dataIndex: "check_out_work_site",
      key: "check_out_work_site",
      render: (site, record) =>
        site === "On-Duty" ? (
          <>
            <p style={{ marginBottom: 4 }}>{site}</p>
            {record?.onduty_type_check_out && (
              <p style={{ marginBottom: 4, fontSize: "12px", color: "#666" }}>
                {record.onduty_type_check_out}
              </p>
            )}
          </>
        ) : (
          <p>{site}</p>
        ),
    },
    {
      title: "Check-Out Photo",
      dataIndex: "check_out_photo_url",
      key: "check_out_photo_url",
      render: (url) =>
        url ? (
          <img
            src={url}
            alt="Check-Out"
            style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 8 }}
          />
        ) : (
          <span>No Image</span>
        ),
    },
    { title: "Total Time", dataIndex: "total_time", key: "total_time" },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  return (
    <>
      {showSingle && (
        <Modal
          open={showSingle}
          footer={null}
          onCancel={() => setShowSingle(false)}
          width={1300}
        >
          <ViewOneEmployeeRecord employee={selectedEmployee} />
        </Modal>
      )}

      {showLead && (
        <Modal
          open={showLead}
          footer={null}
          onCancel={() => {
            setShowLead(false);
            setSelectedLeadDetails(null);
          }}
          width={700}
        >
          {selectedLeadDetails && selectedLeadDetails.length > 0 ? (
            <div style={{ maxHeight: "500px", overflowY: "auto" }}>
              {selectedLeadDetails.map((lead, index) => (
                <Descriptions key={index} bordered column={1} size="small" style={{ marginBottom: 16 }}>
                  <Descriptions.Item label="Lead Number">
                    <strong>{lead.lead_no || "-"}</strong>
                  </Descriptions.Item>
                  <Descriptions.Item label="Customer Name">
                    {lead.customer_name || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {lead.email || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Phone Number">
                    {lead.number || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Project Name">
                    {lead.project_name || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <span
                      style={{
                        padding: "4px 12px",
                        borderRadius: "4px",
                        backgroundColor: lead.status
                          ?.toLowerCase()
                          .includes("cancel")
                          ? "#fff2e8"
                          : "#f6ffed",
                        color: lead.status?.toLowerCase().includes("cancel")
                          ? "#d46b08"
                          : "#52c41a",
                        fontWeight: 500,
                      }}
                    >
                      {lead.status || "-"}
                    </span>
                  </Descriptions.Item>
                </Descriptions>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
              No lead details available
            </div>
          )}
        </Modal>
      )}

      <center>
        <h5>
          <ClockCircleOutlined /> Check-In & Check-Out
        </h5>
      </center>

      <Card
        style={{
          margin: "20px",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <label>Employee Code</label>
            <Input
              placeholder="Enter Employee Code"
              value={draftFilters.employeeCode}
              onChange={(e) => handleFilterChange("employeeCode", e.target.value)}
              allowClear
              onPressEnter={handleSearch}
            />
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <label>Employee Name</label>
            <Input
              placeholder="Enter Employee Name"
              value={draftFilters.employeeName}
              onChange={(e) => handleFilterChange("employeeName", e.target.value)}
              allowClear
              onPressEnter={handleSearch}
            />
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <label>Company Name</label>
            <Select
              placeholder="Select Company"
              style={{ width: "100%" }}
              allowClear
              value={draftFilters.organisation || undefined}
              onChange={(value) =>
                handleFilterChange("organisation", value ?? "")
              }
            >
              {organisations.map((org) => (
                <Option key={org.id} value={org.id}>
                  {org.name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <label>Department</label>
            <Select
              showSearch
              placeholder="Select Department"
              style={{ width: "100%" }}
              allowClear
              value={draftFilters.department || undefined}
              onChange={(value) =>
                handleFilterChange("department", value ?? "")
              }
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
              disabled={!draftFilters.organisation}
            >
              {departments.map((dept) => (
                <Option key={dept.id} value={dept.id}>
                  {dept.name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: "10px" }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <label>Designation</label>
            <Select
              showSearch
              placeholder="Select Designation"
              style={{ width: "100%" }}
              allowClear
              value={draftFilters.designation || undefined}
              onChange={(value) =>
                handleFilterChange("designation", value ?? "")
              }
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
              disabled={!draftFilters.department}
            >
              {designations.map((des) => (
                <Option key={des.id} value={des.id}>
                  {des.name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <label>Date Range</label>
            <RangePicker
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
              value={
                draftFilters.fromDate && draftFilters.toDate
                  ? [dayjs(draftFilters.fromDate), dayjs(draftFilters.toDate)]
                  : null
              }
              onChange={(dates) => {
                if (dates && dates.length === 2) {
                  setDraftFilters((prev) => ({
                    ...prev,
                    fromDate: dates[0],
                    toDate: dates[1],
                  }));
                } else {
                  setDraftFilters((prev) => ({
                    ...prev,
                    fromDate: null,
                    toDate: null,
                  }));
                }
              }}
            />
          </Col>

          {/* Search & Clear Buttons */}
          <Col
            xs={24}
            sm={12}
            md={8}
            lg={10}
            style={{ display: "flex", alignItems: "flex-end", gap: "10px" }}
          >
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
              style={{
                backgroundColor: "#0e497b",
                borderColor: "#0e497b",
                height: "32px",
                minWidth: "100px",
              }}
            >
              Search
            </Button>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClear}
              disabled={loading}
              style={{ height: "32px" }}
            >
              Clear
            </Button>
          </Col>
        </Row>
      </Card>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "15px",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <Button
          icon={<DownloadOutlined />}
          style={{
            height: "36px",
            width: "220px",
            backgroundColor: "#0e497b",
            color: "white",
          }}
          onClick={handleDownload}
          disabled={loading}
        >
          Download{" "}
          {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : "All"}
        </Button>

        {selectedRowKeys.length > 0 && (
          <Button onClick={() => setSelectedRowKeys([])}>
            Clear Selection
          </Button>
        )}
      </div>

      <Spin spinning={loading} tip="Loading attendance records...">
        <Table
          style={{ marginTop: "10px" }}
          columns={columns}
          dataSource={attendanceData}
          bordered
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRecords,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} records`,
            pageSizeOptions: ["10", "20", "50", "100", "200"],
          }}
          onChange={handleTableChange}
          scroll={{ x: "max-content" }}
          rowSelection={rowSelection}
          rowKey="id"
          components={{
            header: {
              cell: (props) => (
                <th
                  {...props}
                  style={{ backgroundColor: "#0e497b", color: "white" }}
                >
                  {props.children}
                </th>
              ),
            },
          }}
        />
      </Spin>
    </>
  );
}